/**
 * Каноническая навигация tenant-app по согласованному дизайну
 * manager v3 (§ 1.6 Nav canonical, Phosphor icons).
 *
 * Источник правды для СТРУКТУРЫ навигации — этот модуль (а НЕ плоский
 * порядок modules[] из /v1/platform/me). Бэкенд по-прежнему решает, что
 * РАЗРЕШЕНО актору: каждый канонический пункт привязан к ключу(ам) модуля
 * (`moduleKeys`), и пункт показывается только если бэкенд вернул хотя бы
 * один из этих модулей в modules[] (RBAC сохраняется) ЛИБО пункт помечен
 * `always: true` (Dashboard / Профиль — доступны при любой роли).
 *
 * Дизайн §1.6 (manager): 17 пунктов в 5 группах
 *   Главное   → Dashboard · Заявки · Проживания
 *   Управление→ Календарь номеров · Номера · Меню · Склад · Персонал · Анонсы
 *   Аналитика → Аналитика заявок · SLA · Отзывы · Поставщики
 *   Аккаунт   → Расписание смен · Подписка/счета · Профиль
 *
 * Состав по ролям регулируется пересечением с modules[] бэкенда:
 * гость видит свой минимум (витрина/меню/заявки/профиль), персонал —
 * операционный набор, менеджер/владелец — полный.
 *
 * Лейблы — через i18n-ключи (@hsc/i18n, namespace common, `nav.*`), 100% RU
 * на русской локали. Никаких humanizeKey-fallback на латиницу.
 */

/** Тип роли для фильтрации видимости (совпадает с DashboardKind). */
export type NavRole = 'owner' | 'manager' | 'staff' | 'guest';

/** Один пункт канонической навигации. */
export interface CanonicalNavItem {
  /** Стабильный ключ пункта (для React key / data-testid). */
  key: string;
  /** i18n-ключ лейбла (namespace common). */
  i18nKey: string;
  /** Имя Phosphor-иконки (см. shared/nav/icons.tsx). */
  icon: string;
  /** Роут внутри SPA. */
  path: string;
  /**
   * Ключи модулей бэкенда, любой из которых разрешает пункт.
   * Если пусто И `always` не задан — пункт скрыт (нет привязки к праву).
   */
  moduleKeys?: readonly string[];
  /** Пункт доступен всегда (Dashboard / Профиль), без проверки modules[]. */
  always?: boolean;
  /** Роли, которым пункт виден. По умолчанию — все. */
  roles?: readonly NavRole[];
  /** Показывать ли как нижний таб на мобайле (и под каким лейблом). */
  bottomTab?: { i18nKey: string; order: number };
}

/** Группа пунктов (визуальный заголовок в сайдбаре). */
export interface CanonicalNavGroup {
  /** Стабильный ключ группы. */
  key: string;
  /** i18n-ключ заголовка группы. */
  i18nKey: string;
  items: readonly CanonicalNavItem[];
}

/**
 * Канонические группы и пункты (порядок/состав/иконки — строго по § 1.6).
 * `bottomTab.order`: 1=Главная 2=Заявки 3=Проживания 4=Персонал
 * (5-й таб «Меню/ещё» добавляется в UI как hamburger-drawer).
 */
export const CANONICAL_NAV: readonly CanonicalNavGroup[] = [
  {
    key: 'main',
    i18nKey: 'nav.group.main',
    items: [
      {
        key: 'dashboard',
        i18nKey: 'nav.dashboard',
        icon: 'house',
        path: '/',
        always: true,
        bottomTab: { i18nKey: 'nav.tab.home', order: 1 },
      },
      {
        key: 'requests',
        i18nKey: 'nav.requests',
        icon: 'list-checks',
        path: '/requests',
        moduleKeys: ['requests'],
        roles: ['owner', 'manager', 'staff', 'guest'],
        bottomTab: { i18nKey: 'nav.requests', order: 2 },
      },
      {
        key: 'stays',
        i18nKey: 'nav.stays',
        icon: 'door-open',
        path: '/stays',
        moduleKeys: ['stays'],
        roles: ['owner', 'manager', 'staff'],
        bottomTab: { i18nKey: 'nav.stays', order: 3 },
      },
    ],
  },
  {
    key: 'ops',
    i18nKey: 'nav.group.ops',
    items: [
      {
        // «Календарь номеров» = шахматка/таймлайн занятости номеров
        // (RoomsChessboard, manager v3 §4.1/§4.4). Отдельный path
        // `/rooms-calendar`, чтобы НЕ конфликтовать с «Расписанием смен»
        // (`/scheduling` → SchedulesPage). Раньше оба пункта вели на
        // /scheduling и «Календарь номеров» открывал графики смен (#40).
        key: 'rooms-calendar',
        i18nKey: 'nav.roomsCalendar',
        icon: 'calendar-blank',
        path: '/rooms-calendar',
        moduleKeys: ['rooms', 'room_status'],
        roles: ['owner', 'manager', 'staff'],
      },
      {
        // «Номера» — статус-доска по настроенным номерам (RoomsBoard): этаж,
        // тип, статус (свободен/занят/уборка/не в строю). Управление списком/
        // типами — в Настройках; занятость по дням — Шахматка (/rooms-calendar).
        key: 'rooms',
        i18nKey: 'nav.rooms',
        icon: 'door',
        path: '/rooms',
        moduleKeys: ['rooms', 'room_status'],
        roles: ['owner', 'manager', 'staff'],
      },
      {
        // Блок 6 «Облака»: экран уборщицы — очередь «На уборку / осмотр».
        // Тот же модуль rooms (RBAC сохранён), но отдельный операционный пункт
        // staff-дизайна §5.2; виден уборщице/менеджеру/владельцу.
        key: 'housekeeping',
        i18nKey: 'nav.housekeeping',
        icon: 'broom',
        path: '/housekeeping',
        moduleKeys: ['rooms', 'room_status'],
        roles: ['owner', 'manager', 'staff'],
      },
      {
        key: 'menu',
        i18nKey: 'nav.menu',
        icon: 'fork-knife',
        path: '/menu',
        moduleKeys: ['menu'],
        roles: ['owner', 'manager', 'staff', 'guest'],
        bottomTab: { i18nKey: 'nav.menu', order: 5 },
      },
      {
        key: 'fnb-queue',
        i18nKey: 'nav.fnbQueue',
        icon: 'fork-knife',
        path: '/fnb-queue',
        moduleKeys: ['menu', 'requests'],
        roles: ['owner', 'manager', 'staff'],
      },
      {
        key: 'staff',
        i18nKey: 'nav.staff',
        icon: 'users-three',
        path: '/staff',
        moduleKeys: ['staff'],
        roles: ['owner', 'manager'],
        bottomTab: { i18nKey: 'nav.staff', order: 4 },
      },
      {
        key: 'events',
        i18nKey: 'nav.events',
        icon: 'sparkle',
        path: '/events',
        moduleKeys: ['events', 'bookable_zones'],
        roles: ['owner', 'manager', 'staff', 'guest'],
      },
    ],
  },
  {
    key: 'analytics',
    i18nKey: 'nav.group.analytics',
    items: [
      {
        key: 'requests-analytics',
        i18nKey: 'nav.requestsAnalytics',
        icon: 'chart-line',
        path: '/requests/analytics',
        moduleKeys: ['requests', 'request_autotasks'],
        roles: ['owner', 'manager'],
      },
      {
        key: 'sla',
        i18nKey: 'nav.sla',
        icon: 'timer',
        path: '/sla',
        moduleKeys: ['requests'],
        roles: ['owner', 'manager'],
      },
      {
        key: 'reviews',
        i18nKey: 'nav.reviews',
        icon: 'star',
        path: '/reviews',
        moduleKeys: ['reviews'],
        roles: ['owner', 'manager'],
      },
    ],
  },
  {
    key: 'account',
    i18nKey: 'nav.group.account',
    items: [
      {
        key: 'scheduling',
        i18nKey: 'nav.scheduling',
        icon: 'calendar-check',
        path: '/scheduling',
        moduleKeys: ['scheduling'],
        roles: ['owner', 'manager'],
      },
      {
        key: 'billing',
        i18nKey: 'nav.billing',
        icon: 'credit-card',
        path: '/billing',
        moduleKeys: ['billing', 'admin_subscription'],
        roles: ['owner', 'manager', 'guest'],
      },
      {
        // «Настройки» (правки Артёма): список номеров + типы номеров (две
        // вкладки). Список номеров и типы перенесены сюда из прежнего пункта
        // «Номера». Управляющая роль; привязан к модулю rooms.
        key: 'settings',
        i18nKey: 'nav.settings',
        icon: 'gear',
        path: '/settings',
        moduleKeys: ['rooms', 'room_status'],
        roles: ['owner', 'manager'],
      },
      {
        key: 'profile',
        i18nKey: 'nav.profile',
        icon: 'user',
        path: '/profile',
        always: true,
      },
    ],
  },
] as const;

/** Резолвленный пункт навигации (готов к рендеру). */
export interface ResolvedNavItem {
  key: string;
  label: string;
  icon: string;
  path: string;
}

/** Резолвленная группа. */
export interface ResolvedNavGroup {
  key: string;
  label: string;
  items: ResolvedNavItem[];
}

/** Функция перевода (совместима с i18next `t`). */
export type TFunc = (key: string) => string;

/**
 * Видим ли канонический пункт при данной роли и наборе разрешённых
 * ключей модулей бэкенда.
 */
export function isItemVisible(
  item: CanonicalNavItem,
  role: NavRole,
  allowedModuleKeys: ReadonlySet<string>,
): boolean {
  if (item.roles && !item.roles.includes(role)) return false;
  if (item.always) return true;
  if (!item.moduleKeys || item.moduleKeys.length === 0) return false;
  return item.moduleKeys.some((k) => allowedModuleKeys.has(k));
}

/**
 * Строит сгруппированную навигацию для роли, пересекая каноническую
 * структуру с разрешёнными модулями бэкенда. Лейблы — через t() (i18n).
 * Пустые группы (без видимых пунктов) отбрасываются.
 */
export function buildCanonicalNav(
  role: NavRole,
  moduleKeys: readonly string[],
  t: TFunc,
): ResolvedNavGroup[] {
  const allowed = new Set(moduleKeys);
  const groups: ResolvedNavGroup[] = [];
  for (const group of CANONICAL_NAV) {
    const items: ResolvedNavItem[] = [];
    for (const item of group.items) {
      if (!isItemVisible(item, role, allowed)) continue;
      items.push({
        key: item.key,
        label: t(item.i18nKey),
        icon: item.icon,
        path: item.path,
      });
    }
    if (items.length > 0) {
      groups.push({ key: group.key, label: t(group.i18nKey), items });
    }
  }
  return groups;
}

/** Резолвленный нижний таб (мобайл). */
export interface ResolvedBottomTab {
  key: string;
  label: string;
  icon: string;
  path: string;
}

/**
 * Строит до 4 первичных нижних табов (Главная/Заявки/Проживания/Персонал)
 * из видимых пунктов; 5-й таб («Меню/ещё») UI добавляет сам как hamburger.
 * Сохраняет порядок bottomTab.order. Отбрасывает невидимые роли/модули.
 */
export function buildBottomTabs(
  role: NavRole,
  moduleKeys: readonly string[],
  t: TFunc,
): ResolvedBottomTab[] {
  const allowed = new Set(moduleKeys);
  const tabs: { order: number; tab: ResolvedBottomTab }[] = [];
  for (const group of CANONICAL_NAV) {
    for (const item of group.items) {
      if (!item.bottomTab) continue;
      // «Меню» — это 5-й таб, его рендерит UI как hamburger-кнопку.
      if (item.bottomTab.order >= 5) continue;
      if (!isItemVisible(item, role, allowed)) continue;
      tabs.push({
        order: item.bottomTab.order,
        tab: {
          key: item.key,
          label: t(item.bottomTab.i18nKey),
          icon: item.icon,
          path: item.path,
        },
      });
    }
  }
  tabs.sort((a, b) => a.order - b.order);
  return tabs.map((x) => x.tab);
}
